// Risk donut — FE-034 / spec §7.0.9.
//
// Tier 1 ships an SVG donut + accessible `<dl>` summary. The
// donut is purely decorative; the canonical data is the list, so
// screen-reader users see the same information without needing
// the chart. Heavy chart libraries are deferred to the FE-037 CFD
// chunk.

import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  type DashboardDateRange,
  type RiskWorkloadResponse,
} from "@/features/dashboard";


type RiskBucket = "critical" | "high" | "medium" | "low";


interface RiskDonutProps {
  data: RiskWorkloadResponse["risk_donut"];
  dateRange: DashboardDateRange;
}


const BUCKET_ORDER: RiskBucket[] = [
  "critical", "high", "medium", "low",
];


const BUCKET_META: Record<
  RiskBucket,
  { label: string; segment: string; swatch: string; helper: string }
> = {
  critical: {
    label: "Critical",
    segment: "stroke-ember",
    swatch: "bg-ember",
    helper: "3+ overdue or 2+ blocked Commitments",
  },
  high: {
    label: "High",
    segment: "stroke-blocked",
    swatch: "bg-blocked",
    helper: "1+ overdue or 1+ blocked Commitments",
  },
  medium: {
    label: "Medium",
    segment: "stroke-gold",
    swatch: "bg-gold",
    helper: "Open Commitments, none overdue or blocked",
  },
  low: {
    label: "Low",
    segment: "stroke-sage",
    swatch: "bg-sage",
    helper: "No open risky work",
  },
};


export function RiskDonut({ data, dateRange }: RiskDonutProps) {
  const total = BUCKET_ORDER.reduce((sum, b) => sum + (data[b] ?? 0), 0);
  const radius = 36;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;

  let cursor = 0;
  const segments = BUCKET_ORDER.map((bucket) => {
    const value = data[bucket] ?? 0;
    if (total === 0 || value === 0) return null;
    const fraction = value / total;
    const length = fraction * circumference;
    const offset = -cursor * circumference;
    cursor += fraction;
    return { bucket, length, offset };
  }).filter((s): s is { bucket: RiskBucket; length: number; offset: number } =>
    s !== null,
  );

  return (
    <div data-testid="risk-donut" className="flex flex-col items-center gap-3 sm:flex-row sm:items-stretch">
      <div className="relative flex h-32 w-32 flex-shrink-0 items-center justify-center">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle
            cx="50" cy="50" r={radius}
            fill="none"
            className="stroke-recessed"
            strokeWidth={stroke}
          />
          {segments.map((seg) => (
            <circle
              key={seg.bucket}
              data-testid={`risk-donut-segment-${seg.bucket}`}
              cx="50" cy="50" r={radius}
              fill="none"
              className={BUCKET_META[seg.bucket].segment}
              strokeWidth={stroke}
              strokeDasharray={`${seg.length} ${circumference - seg.length}`}
              strokeDashoffset={seg.offset}
            />
          ))}
        </svg>
        <div className="absolute flex flex-col items-center text-text-primary">
          <span
            className="text-lg font-semibold"
            data-testid="risk-donut-total"
          >
            {total}
          </span>
          <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
            accounts
          </span>
        </div>
      </div>

      <ul
        data-testid="risk-donut-summary"
        aria-label="Account risk distribution"
        className="flex flex-1 flex-col gap-1 text-sm"
      >
        {BUCKET_ORDER.map((bucket) => {
          const value = data[bucket] ?? 0;
          const meta = BUCKET_META[bucket];
          const drillUrl = buildAccountFilterUrl(
            { risk_level: bucket }, dateRange,
          );
          const swatch = (
            <span
              aria-hidden="true"
              className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${meta.swatch}`}
            />
          );
          const body = (
            <DonutRowBody label={meta.label} helper={meta.helper} value={value} />
          );
          return (
            <li key={bucket} data-testid={`risk-donut-row-${bucket}`}>
              {value > 0 ? (
                <Link
                  to={drillUrl}
                  data-testid={`risk-donut-link-${bucket}`}
                  aria-label={
                    `${meta.label}: ${value} account${value === 1 ? "" : "s"}`
                  }
                  className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-recessed"
                >
                  {swatch}
                  {body}
                </Link>
              ) : (
                <div
                  data-testid={`risk-donut-row-empty-${bucket}`}
                  className="flex items-center gap-2 px-1 py-0.5"
                >
                  {swatch}
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}


function DonutRowBody({
  label, helper, value,
}: { label: string; helper: string; value: number }) {
  return (
    <span className="flex flex-1 items-center justify-between gap-2">
      <span className="flex flex-col">
        <span className="text-sm font-medium text-text-primary">{label}</span>
        <span className="text-xs text-text-tertiary">{helper}</span>
      </span>
      <span className="text-sm font-semibold tabular-nums text-text-primary">
        {value}
      </span>
    </span>
  );
}
